import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, BadgeCheck, Clock, XCircle, Upload, ArrowLeft, IdCard, Camera } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_META = {
  none: { icon: ShieldCheck, color: "#A0A0A0", label: "Not verified" },
  pending: { icon: Clock, color: "#D4AF37", label: "Under review" },
  approved: { icon: BadgeCheck, color: "#22c55e", label: "Verified" },
  rejected: { icon: XCircle, color: "#E63946", label: "Rejected" },
};

function FileField({ testid, label, hint, icon: Icon, file, onChange }) {
  return (
    <div>
      <div className="text-sm text-white font-medium mb-1 flex items-center gap-2"><Icon size={16} className="text-[#D4AF37]" /> {label}</div>
      <div className="text-xs text-[#A0A0A0] mb-2">{hint}</div>
      <label data-testid={`${testid}-label`} className="flex items-center gap-3 cursor-pointer pe-input rounded-lg px-4 py-3 hover:border-[#D4AF37]/50 transition-colors">
        <Upload size={18} className="text-[#D4AF37]" />
        <span className="text-sm text-[#A0A0A0] truncate flex-1">{file ? file.name : "Choose file (JPG, PNG, WEBP or PDF)"}</span>
        <input data-testid={testid} type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={(e) => onChange(e.target.files?.[0] || null)} className="hidden" />
      </label>
    </div>
  );
}

export default function Verify() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [legalName, setLegalName] = useState("");
  const [dob, setDob] = useState("");
  const [idDoc, setIdDoc] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/verification/status");
      setStatus(data);
    } catch (e) { setStatus({ status: "none", verified: false }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!legalName.trim() || !dob) { toast.error("Enter your legal name and date of birth"); return; }
    if (!idDoc || !selfie) { toast.error("Upload both your ID document and a selfie"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("legal_name", legalName.trim());
      fd.append("date_of_birth", dob);
      fd.append("id_document", idDoc);
      fd.append("selfie", selfie);
      await api.post("/verification/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Submitted! Our team will review your verification shortly.");
      await refreshUser();
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const st = status?.status || "none";
  const meta = STATUS_META[st] || STATUS_META.none;
  const StatusIcon = meta.icon;
  const canSubmit = st === "none" || st === "rejected";

  return (
    <div className="p-6 md:p-12 max-w-2xl mx-auto">
      <button data-testid="verify-back" onClick={() => navigate("/profile")}
        className="text-[#A0A0A0] hover:text-white text-sm flex items-center gap-2 mb-4">
        <ArrowLeft size={16} /> Back to profile
      </button>
      <div className="overline mb-1">Trust & Safety</div>
      <h2 className="font-heading text-3xl md:text-4xl mb-2">Get Verified</h2>
      <p className="text-[#A0A0A0] text-sm mb-8 max-w-lg">
        Confirm you're 18+ and who you say you are. Verified members earn a gold badge, build trust,
        and unlock creator payouts. Your documents are stored privately and only reviewed by our team.
      </p>

      {/* Status banner */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        data-testid="verify-status-banner"
        className="pe-card rounded-2xl p-5 mb-6 flex items-center gap-4">
        <StatusIcon size={32} style={{ color: meta.color }} />
        <div className="flex-1">
          <div className="font-medium text-white flex items-center gap-2">{meta.label}
            {st === "approved" && <BadgeCheck size={16} className="text-[#D4AF37]" />}
          </div>
          {st === "pending" && <div className="text-xs text-[#A0A0A0] mt-1">Submitted {status?.submitted_at ? new Date(status.submitted_at).toLocaleString() : ""}. We'll notify you once reviewed.</div>}
          {st === "rejected" && <div className="text-xs text-[#E63946] mt-1">Reason: {status?.reject_reason || "Did not meet requirements"}. Please resubmit below.</div>}
          {st === "approved" && <div className="text-xs text-[#A0A0A0] mt-1">Your account is verified. The gold badge now appears on your profile.</div>}
          {st === "none" && <div className="text-xs text-[#A0A0A0] mt-1">You haven't submitted verification yet.</div>}
        </div>
      </motion.div>

      {canSubmit && (
        <div className="pe-card rounded-2xl p-6 space-y-5">
          <div>
            <div className="text-sm text-white font-medium mb-1">Full legal name</div>
            <input data-testid="verify-legal-name" placeholder="As shown on your ID" value={legalName}
              onChange={(e) => setLegalName(e.target.value)} className="pe-input w-full rounded-lg px-4 py-3" />
          </div>
          <div>
            <div className="text-sm text-white font-medium mb-1">Date of birth</div>
            <input data-testid="verify-dob" type="date" value={dob}
              onChange={(e) => setDob(e.target.value)} className="pe-input w-full rounded-lg px-4 py-3" />
          </div>
          <FileField testid="verify-id-doc" icon={IdCard} label="Government-issued ID"
            hint="Passport, driver's licence or national ID. Make sure all details are clearly visible."
            file={idDoc} onChange={setIdDoc} />
          <FileField testid="verify-selfie" icon={Camera} label="Verification selfie"
            hint="A clear photo of yourself holding your ID next to your face."
            file={selfie} onChange={setSelfie} />
          <button data-testid="verify-submit" onClick={submit} disabled={submitting}
            className="btn-gold w-full rounded-full py-3 disabled:opacity-60">
            {submitting ? "Submitting…" : "Submit for verification"}
          </button>
          <p className="text-[11px] text-[#6b6b6b] text-center">
            By submitting you confirm the documents are genuine and belong to you. False submissions result in a permanent ban.
          </p>
        </div>
      )}
    </div>
  );
}
